import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";

import PipelineService, { pipelineErrorMessage } from "../../services/pipeline.service";
import { IPipelineStatus, IStartServiceRequest } from "../../types/api/pipeline.type";
import useNotifications from "../../components/NotificationProvider";
import useLoader from "../../components/LoaderProvider";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import {
  CONTROLLABLE_SERVICES,
  DEFAULT_STARTPOINT,
  ServiceStateChip,
  localDatetimeToUtcIso,
  startpointOptions,
  workerLabel,
} from "./common";

interface IServicesTab {
  envId: string;
  status: IPipelineStatus | null;
  loading: boolean;
  reload: () => Promise<void>;
}

const StartServiceDialog = ({
  service,
  configuredWorkers,
  onClose,
  onStart,
}: {
  service: string;
  configuredWorkers?: number;
  onClose: () => void;
  onStart: (body: IStartServiceRequest) => void;
}) => {
  const options = startpointOptions(service);
  const [workers, setWorkers] = useState("");
  const [startpoint, setStartpoint] = useState(DEFAULT_STARTPOINT[service] ?? "queue");
  const [customStartpoint, setCustomStartpoint] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [enqueueAlerts, setEnqueueAlerts] = useState(true);

  const destructive = options.find((o) => o.value === startpoint)?.destructive;
  const workersInvalid = workers !== "" && !(Number.isInteger(Number(workers)) && Number(workers) > 0);
  const customMissing = startpoint === "custom" && !customStartpoint;

  const submit = () => {
    const body: IStartServiceRequest = {
      startpoint: startpoint === "custom" ? localDatetimeToUtcIso(customStartpoint) : startpoint,
    };
    if (workers) body.workers = Number(workers);
    if (service === "resample" && endpoint) body.endpoint = localDatetimeToUtcIso(endpoint);
    if (service === "score") body.enqueue_alerts = enqueueAlerts;
    onStart(body);
  };

  return (
    <Dialog fullWidth maxWidth="sm" open onClose={onClose}>
      <DialogTitle>Start {service}</DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={workerLabel(service)}
            type="number"
            value={workers}
            onChange={(e) => setWorkers(e.target.value)}
            error={workersInvalid}
            helperText={
              workersInvalid
                ? "Must be a whole number above 0"
                : `Leave blank for the configured default (${configuredWorkers ?? "n/a"})`
            }
            inputProps={{ min: 1 }}
          />
          <FormControl fullWidth>
            <InputLabel id={`${service}-startpoint-label`}>Startpoint</InputLabel>
            <Select
              labelId={`${service}-startpoint-label`}
              label="Startpoint"
              value={startpoint}
              onChange={(e) => setStartpoint(e.target.value)}
            >
              {options.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {destructive && (
            <Alert severity="warning">
              This clears {service}'s stream before starting. Queued tasks are lost; it then resumes from
              the last watermark.
            </Alert>
          )}
          {startpoint === "custom" && (
            <TextField
              label="Custom startpoint (UTC)"
              type="datetime-local"
              value={customStartpoint}
              onChange={(e) => setCustomStartpoint(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          )}
          {service === "resample" && (
            <TextField
              label="Endpoint (UTC, optional)"
              type="datetime-local"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Bounded backfill: resample stops by itself when it reaches this time"
            />
          )}
          {service === "score" && (
            <FormControlLabel
              control={
                <Checkbox checked={enqueueAlerts} onChange={(e) => setEnqueueAlerts(e.target.checked)} />
              }
              label="Enqueue alert tasks after scoring (turn off during backfills to avoid an alert flood)"
            />
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button sx={{ mr: 2 }} onClick={onClose}>
          Close
        </Button>
        <Button
          sx={{ mr: 2 }}
          variant="contained"
          color={destructive ? "error" : "primary"}
          disabled={workersInvalid || customMissing}
          onClick={submit}
        >
          {destructive ? "Clear stream & start" : "Start"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ScaleServiceDialog = ({
  service,
  current,
  onClose,
  onScale,
}: {
  service: string;
  current?: number;
  onClose: () => void;
  onScale: (workers: number) => void;
}) => {
  const [workers, setWorkers] = useState(current ? String(current) : "");
  const valid = Number.isInteger(Number(workers)) && Number(workers) > 0;

  return (
    <Dialog fullWidth maxWidth="xs" open onClose={onClose}>
      <DialogTitle>Scale {service}</DialogTitle>
      <Divider />
      <DialogContent>
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          autoFocus
          label={workerLabel(service)}
          type="number"
          value={workers}
          onChange={(e) => setWorkers(e.target.value)}
          error={workers !== "" && !valid}
          helperText="The service restarts with the new value, keeping its startpoint"
          inputProps={{ min: 1 }}
        />
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button sx={{ mr: 2 }} onClick={onClose}>
          Close
        </Button>
        <Button sx={{ mr: 2 }} variant="contained" disabled={!valid} onClick={() => onScale(Number(workers))}>
          Scale
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ServicesTab = ({ envId, status, loading, reload }: IServicesTab) => {
  const { setNotification } = useNotifications();
  const { setLoading } = useLoader();
  const [startFor, setStartFor] = useState<string | null>(null);
  const [scaleFor, setScaleFor] = useState<string | null>(null);
  const [stopFor, setStopFor] = useState<string | null>(null);

  const run = async (action: () => Promise<unknown>, success: string) => {
    try {
      setLoading(true);
      await action();
      setNotification({ message: success, open: true });
    } catch (e) {
      setNotification({ message: pipelineErrorMessage(e), open: true, type: "error" });
    } finally {
      setLoading(false);
      await reload();
    }
  };

  const services = CONTROLLABLE_SERVICES.filter((name) => status?.services[name]);

  if (!status) {
    return loading ? null : <Alert severity="info">Service status is unavailable.</Alert>;
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table aria-label="pipeline services">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Service</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>State</TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="right">
                Workers
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="right">
                Queue
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="right">
                PEL
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="right">
                DLQ
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {services.map((name) => {
              const s = status.services[name];
              return (
                <TableRow key={name} hover>
                  <TableCell>{name}</TableCell>
                  <TableCell>
                    <ServiceStateChip status={s} />
                  </TableCell>
                  <TableCell align="right" title={workerLabel(name)}>
                    {s.worker_count ?? 0}
                  </TableCell>
                  <TableCell align="right">{s.queue_depth ?? "—"}</TableCell>
                  <TableCell align="right">{s.pel_count ?? "—"}</TableCell>
                  <TableCell align="right">{s.dlq_depth ?? "—"}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ whiteSpace: "nowrap" }}>
                      <Button size="small" variant="contained" sx={{ mr: 1 }} onClick={() => setStartFor(name)}>
                        Start…
                      </Button>
                      <Button size="small" sx={{ mr: 1 }} onClick={() => setScaleFor(name)}>
                        Scale…
                      </Button>
                      <Button size="small" color="error" onClick={() => setStopFor(name)}>
                        Stop
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {startFor && (
        <StartServiceDialog
          service={startFor}
          configuredWorkers={status.configured_workers?.[startFor]}
          onClose={() => setStartFor(null)}
          onStart={(body) => {
            const service = startFor;
            setStartFor(null);
            run(() => PipelineService.startService(envId, service, body), `${service} started`);
          }}
        />
      )}
      {scaleFor && (
        <ScaleServiceDialog
          service={scaleFor}
          current={status.services[scaleFor]?.worker_count}
          onClose={() => setScaleFor(null)}
          onScale={(workers) => {
            const service = scaleFor;
            setScaleFor(null);
            run(() => PipelineService.scaleService(envId, service, workers), `${service} scaled to ${workers}`);
          }}
        />
      )}
      {stopFor && (
        // Keyed + only mounted while open: ConfirmationDialog keeps its typed
        // text in its own form state, which must not carry over between stops.
        <ConfirmationDialog
          key={stopFor}
          open
          setOpen={(open) => !open && setStopFor(null)}
          title={`Stop ${stopFor}`}
          content={`${stopFor} stops processing in this tenant and stays stopped (it won't auto-restart) until started again.`}
          confirmationText={stopFor}
          onProceed={() => {
            const service = stopFor;
            setStopFor(null);
            run(() => PipelineService.stopService(envId, service), `${service} stopped`);
          }}
        />
      )}
    </>
  );
};

export default ServicesTab;
